import React, { useState, useEffect, useRef } from 'react';
import { database, storage } from '../firebaseConfig';
import { ref as dbRef, onValue, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useActiveWorld } from '../hooks/useActiveWorld';
import './ScreenComposerSettings.css';

const ScreenComposerSettings = () => {
  const { userId } = useActiveWorld();
  const [settings, setSettings] = useState({
    defaultUrl: '',
    format: 'image',
    photoUrl: '',
    videoUrl: '',
  });
  const [savedSettings, setSavedSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [error, setError] = useState(null);

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
    const unsub = onValue(settingsDbRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const loaded = {
          defaultUrl: data.defaultUrl || '',
          format: data.format || 'image',
          photoUrl: data.photoUrl || '',
          videoUrl: data.videoUrl || '',
        };
        setSettings(loaded);
        setSavedSettings(loaded);
      } else {
        setSavedSettings({ defaultUrl: '', format: 'image', photoUrl: '', videoUrl: '' });
      }
      setLoading(false);
    });

    return () => unsub();
  }, [userId]);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    setError(null);

    try {
      const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
      await update(settingsDbRef, {
        defaultUrl: settings.defaultUrl.trim(),
        format: settings.format,
        photoUrl: settings.photoUrl,
        videoUrl: settings.videoUrl,
      });
      setSavedSettings({ ...settings });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    } catch (err) {
      console.error('Error saving Screen Composer settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      setError('Photo must be under 1 MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, WebP)');
      return;
    }

    setUploadingPhoto(true);
    setError(null);

    try {
      const fileRef = storageRef(storage, `screenComposer/${userId}/photo_${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      setSettings(prev => ({ ...prev, photoUrl: url }));

      const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
      await update(settingsDbRef, { photoUrl: url });
      setSavedSettings(prev => prev ? { ...prev, photoUrl: url } : prev);
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Video must be under 5 MB');
      return;
    }

    if (!file.type.startsWith('video/')) {
      setError('Please select a video file (MP4, MOV, WebM)');
      return;
    }

    setUploadingVideo(true);
    setError(null);

    try {
      const fileRef = storageRef(storage, `screenComposer/${userId}/video_${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      setSettings(prev => ({ ...prev, videoUrl: url }));

      const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
      await update(settingsDbRef, { videoUrl: url });
      setSavedSettings(prev => prev ? { ...prev, videoUrl: url } : prev);
    } catch (err) {
      console.error('Error uploading video:', err);
      setError('Failed to upload video');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    if (!settings.photoUrl) return;
    setError(null);

    try {
      try {
        const fileRef = storageRef(storage, settings.photoUrl);
        await deleteObject(fileRef);
      } catch { /* file may not exist */ }

      setSettings(prev => ({ ...prev, photoUrl: '' }));
      const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
      await update(settingsDbRef, { photoUrl: '' });
      setSavedSettings(prev => prev ? { ...prev, photoUrl: '' } : prev);
    } catch (err) {
      console.error('Error removing photo:', err);
    }
  };

  const handleRemoveVideo = async () => {
    if (!settings.videoUrl) return;
    setError(null);

    try {
      try {
        const fileRef = storageRef(storage, settings.videoUrl);
        await deleteObject(fileRef);
      } catch { /* file may not exist */ }

      setSettings(prev => ({ ...prev, videoUrl: '' }));
      const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
      await update(settingsDbRef, { videoUrl: '' });
      setSavedSettings(prev => prev ? { ...prev, videoUrl: '' } : prev);
    } catch (err) {
      console.error('Error removing video:', err);
    }
  };

  const hasChanges = savedSettings && (
    settings.defaultUrl.trim() !== savedSettings.defaultUrl ||
    settings.format !== savedSettings.format
  );

  if (loading) {
    return (
      <div className="sc-settings-container">
        <div className="sc-settings-loading">
          <div className="sc-settings-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sc-settings-container">
      <div className="sc-settings-header">
        <div className="sc-settings-header-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 21H16M12 17V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <div className="sc-settings-header-text">
          <h2>Screen Composer</h2>
          <p>Generate personalized screenshots and GIFs of websites to embed in your emails.</p>
        </div>
      </div>

      {error && (
        <div className="sc-settings-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Default URL */}
      <div className="sc-settings-card">
        <div className="sc-settings-card-header">
          <div className="sc-settings-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h3>Default Website URL</h3>
            <p>The website that will be captured by default. You can override this per email.</p>
          </div>
        </div>
        <input
          type="url"
          className="sc-settings-url-input"
          placeholder="https://your-website.com"
          value={settings.defaultUrl}
          onChange={(e) => setSettings(prev => ({ ...prev, defaultUrl: e.target.value }))}
        />
      </div>

      {/* Output Format */}
      <div className="sc-settings-card">
        <div className="sc-settings-card-header">
          <div className="sc-settings-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
              <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h3>Default Output Format</h3>
            <p>Choose whether to generate a static image or an animated scroll GIF.</p>
          </div>
        </div>
        <div className="sc-settings-format-toggle">
          <button
            className={`sc-settings-format-btn ${settings.format === 'image' ? 'active' : ''}`}
            onClick={() => setSettings(prev => ({ ...prev, format: 'image' }))}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
              <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="sc-format-label">Screenshot</span>
            <span className="sc-format-desc">Static JPEG image</span>
          </button>
          <button
            className={`sc-settings-format-btn ${settings.format === 'video' ? 'active' : ''}`}
            onClick={() => setSettings(prev => ({ ...prev, format: 'video' }))}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="2" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
              <polygon points="10,8 16,12 10,16" fill="currentColor"/>
            </svg>
            <span className="sc-format-label">Scroll GIF</span>
            <span className="sc-format-desc">~4s animated scroll</span>
          </button>
        </div>
      </div>

      {/* Photo Overlay */}
      <div className="sc-settings-card">
        <div className="sc-settings-card-header">
          <div className="sc-settings-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 21V19C20 16.7909 18.2091 15 16 15H8C5.79086 15 4 16.7909 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div>
            <h3>Profile Photo Overlay</h3>
            <p>Upload a profile photo that will be overlaid on the screenshot. Max 1 MB.</p>
          </div>
        </div>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handlePhotoUpload}
        />
        {settings.photoUrl ? (
          <div className="sc-settings-asset-preview">
            <img src={settings.photoUrl} alt="Profile overlay" />
            <div className="sc-settings-asset-actions">
              <button className="sc-asset-change-btn" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}>
                {uploadingPhoto ? 'Uploading...' : 'Change'}
              </button>
              <button className="sc-asset-remove-btn" onClick={handleRemovePhoto}>Remove</button>
            </div>
          </div>
        ) : (
          <button
            className="sc-settings-upload-btn"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
          >
            {uploadingPhoto ? (
              <><span className="sc-btn-spinner"></span> Uploading...</>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Photo
              </>
            )}
          </button>
        )}
      </div>

      {/* Video Overlay */}
      <div className="sc-settings-card">
        <div className="sc-settings-card-header">
          <div className="sc-settings-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div>
            <h3>Webcam Video Overlay</h3>
            <p>Upload a webcam video that will be overlaid on the capture. Max 5 MB.</p>
          </div>
        </div>
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          style={{ display: 'none' }}
          onChange={handleVideoUpload}
        />
        {settings.videoUrl ? (
          <div className="sc-settings-asset-preview">
            <video src={settings.videoUrl} controls style={{ maxWidth: '200px', borderRadius: '10px' }} />
            <div className="sc-settings-asset-actions">
              <button className="sc-asset-change-btn" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo}>
                {uploadingVideo ? 'Uploading...' : 'Change'}
              </button>
              <button className="sc-asset-remove-btn" onClick={handleRemoveVideo}>Remove</button>
            </div>
          </div>
        ) : (
          <button
            className="sc-settings-upload-btn"
            onClick={() => videoInputRef.current?.click()}
            disabled={uploadingVideo}
          >
            {uploadingVideo ? (
              <><span className="sc-btn-spinner"></span> Uploading...</>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Video
              </>
            )}
          </button>
        )}
      </div>

      {/* Save Button */}
      <div className="sc-settings-bottom-actions">
        <button
          className={`sc-settings-save-btn ${isSaved ? 'saved' : ''}`}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? (
            <><span className="sc-btn-spinner"></span> Saving...</>
          ) : isSaved ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Saved!
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  );
};

export default ScreenComposerSettings;
